import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Sparkles, GraduationCap, LineChart,
  Award, Briefcase, LogOut, Menu, X, Terminal, Rocket, BarChart3, UserSquare2, Shield, Settings,
  CalendarClock, Notebook, Target, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds";

const NAV_ITEMS = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: NAV.dashboardLink },
  { to: "/app/tutor", label: "Learning Sessions", icon: Sparkles, testid: NAV.tutorLink },
  { to: "/app/paths", label: "Learning Paths", icon: GraduationCap, testid: NAV.pathsLink },
  { to: "/app/coding", label: "Coding Lab", icon: Terminal, testid: "nav-coding-link" },
  { to: "/app/projects", label: "Projects", icon: Rocket, testid: "nav-projects-link" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics-link" },
  { to: "/app/portfolio", label: "Portfolio", icon: UserSquare2, testid: "nav-portfolio-link" },
  { to: "/app/progress", label: "Progress", icon: LineChart, testid: NAV.progressLink },
  { to: "/app/quiz-stats", label: "Quiz Stats", icon: Target, testid: "nav-quiz-stats-link" },
  { to: "/app/certificates", label: "Certificates", icon: Award, testid: NAV.certificatesLink },
  { to: "/app/enterprise", label: "Enterprise", icon: Building2, testid: "nav-enterprise-link" },
  { to: "/app/coach", label: "Career Coach", icon: Briefcase, testid: NAV.coachLink },
  { to: "/app/planner", label: "Study Planner", icon: CalendarClock, testid: "nav-planner-link" },
  { to: "/app/notebook", label: "Mentor's Notebook", icon: Notebook, testid: "nav-notebook-link" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings-link" },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* Sidebar (desktop) */}
      <aside className="fixed inset-y-0 left-0 hidden w-[260px] flex-col border-r border-border bg-card/40 backdrop-blur-xl md:flex z-40">
        <div className="flex items-center gap-2 px-6 py-6">
          <Link data-testid={NAV.logoLink} to="/app/dashboard" className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
            <div>
              <div className="font-display text-lg font-black tracking-tighter leading-none">LaneX</div>
              <div className="label-mono text-[0.6rem]">Academy</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {NAV_ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all
                ${isActive
                  ? "bg-primary/10 text-primary border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent"
                }`
              }
            >
              <it.icon className="h-4 w-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-full bg-secondary font-mono text-sm">
              {(user?.name?.[0] || "L").toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.name || "Learner"}</div>
              <div className="flex items-center gap-2 label-mono text-[0.6rem]">
                <span className="text-primary">L{user?.level || 1}</span>
                <span>·</span>
                <span>{user?.xp || 0} XP</span>
              </div>
            </div>
            {user?.role === "super_admin" && (
              <NavLink to="/app/admin" className="rounded-full border border-primary/40 bg-primary/10 p-1.5 text-primary" title="Admin">
                <Shield className="h-3.5 w-3.5" />
              </NavLink>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              data-testid={NAV.logoutBtn}
              variant="outline"
              size="sm"
              className="flex-1 rounded-full font-mono text-xs uppercase tracking-widest"
              onClick={async () => { await logout(); navigate("/"); }}
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-4 py-3 md:hidden">
        <Link to="/app/dashboard" className="flex items-center gap-2">
          <img src="/lanex-mark.png" alt="LaneX Academy" className="h-9 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_12px_rgba(0,238,199,0.45)]" />
          <span className="font-display text-base font-black">LaneX</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button
            data-testid={NAV.mobileMenuBtn}
            variant="ghost"
            size="icon"
            onClick={() => setOpenMobile((o) => !o)}
          >
            {openMobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>
      {openMobile && (
        <div className="fixed inset-x-0 top-[57px] z-30 border-b border-border bg-background/95 backdrop-blur-xl md:hidden">
          <nav className="space-y-1 p-3">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpenMobile(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"}`
                }
              >
                <it.icon className="h-4 w-4" /> {it.label}
              </NavLink>
            ))}
            <Button
              variant="outline"
              className="mt-2 w-full rounded-full"
              onClick={async () => { await logout(); navigate("/"); }}
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </nav>
        </div>
      )}

      {/* Content */}
      <main className="relative z-10 md:pl-[260px]">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
